<template>

    <div class="user-menu">

        <b-navbar toggleable="lg" type="light">

            <back-nav></back-nav>

            <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

            <b-badge class="main-menu__environment">
                <span v-if="$store.state.environment != 'production'">
                    {{ $store.state.environment }}
                </span>
            </b-badge>

            <b-collapse id="nav-collapse" class="main-menu" is-nav>

                <!-- Right aligned nav items -->
                <b-navbar-nav class="ml-auto">

                    <!-- guest menu -->

                    <b-nav-item to="/"
                                v-if="$user.noLoggedInUser()"
                                :disabled="isHomeDisabled"
                                :title="isHomeTitle">
                        {{ $i18n.gt('LABEL_HOME') }}
                    </b-nav-item>

                    <b-nav-item @click="switchLanguage()">
                        {{ $user.switchLanguageLabel() }}
                    </b-nav-item>

                    <!-- practice links (for practitioners) -->
                    <b-nav-item-dropdown text="My Practice" right
                                         v-if="$user.roleIs('PRACTITIONER')">
                        <b-dropdown-item :to="`/practice/profile/id/${$user.practitionerPracticeId()}`"
                                         v-if="$auth.canManagePracticeStaff()">
                            {{ $i18n.gt('LABEL_EDIT_PRACTICE') }}
                        </b-dropdown-item>

                        <b-dropdown-item :to="`/practice/practitionerAdmin`"
                                         v-if="$auth.canManagePracticeStaff()">
                            {{ $i18n.gt('LABEL_MANAGE_PRACTITIONERS') }}
                        </b-dropdown-item>

                        <b-dropdown-item :to="`/companyPractitioner/plans/id/${$user.currentPractice().company_id}`"
                                         v-if="$auth.canManageCompanyPlans()">
                            {{ $i18n.gt('LABEL_MANAGE_PLANS') }}
                        </b-dropdown-item>

                        <b-dropdown-item :to="`/practice/plans/id/${$user.practitionerPracticeId()}`"
                                         v-if="$auth.canManagePracticeResources()">
                            {{ $i18n.gt('LABEL_SELECT_PLANS') }}
                        </b-dropdown-item>

                        <b-dropdown-item :to="`/practice/superbillCodes/id/${$user.practitionerPracticeId()}`"
                                         v-if="$auth.canManagePracticeResources()">
                            {{ $i18n.gt('LABEL_SUPERBILL_CODES') }}
                        </b-dropdown-item>

                        <b-dropdown-item :to="`/practice/documents/id/${$user.practitionerPracticeId()}`"
                                         v-if="$auth.canManagePracticeResources()">
                            {{ $i18n.gt('LABEL_PRACTICE_DOCUMENTS') }}
                        </b-dropdown-item>

                        <b-dropdown-item :to="`/practice/messages/id/${$user.practitionerPracticeId()}`"
                                         v-if="$auth.canManagePatients()">
                            {{ $i18n.gt('LABEL_MESSAGE_TEMPLATES') }}
                        </b-dropdown-item>

                        <b-dropdown-item :to="`/practice/appointments/id/${$user.practitionerId()}`"
                                           v-if="$auth.canManageAppointments()">
                            {{ $i18n.gt('LABEL_APPOINTMENTS') }}
                        </b-dropdown-item>

                        <b-dropdown-item :to="`/video-call`"
                                         v-if="!inProduction && !inDemo">
                            {{ $i18n.gt('LABEL_VIDEO_CALL') }}
                        </b-dropdown-item>

                        <b-dropdown-item :to="`/practitioner/${$user.practitionerPracticeId()}/clientReports`">
                            Reports
                        </b-dropdown-item>

                    </b-nav-item-dropdown>

                    <!-- dropdown profile/switch-practice/masking links (for practitioners) -->
                    <b-nav-item-dropdown :text="`${$user.email()} (${$user.role()})`" right
                                         v-if="$user.roleIs('PRACTITIONER')">
                        <b-dropdown-item :to="`/practitioner/profile/id/${$user.participantId()}`">
                            {{ $i18n.gt('LABEL_EDIT_PROFILE') }}
                        </b-dropdown-item>

                        <template v-if="otherPractices.length">
                            <b-dropdown-item>
                                {{ $i18n.gt('LABEL_CURRENT_PRACTICE') }}: {{ currentPractice.name }}
                            </b-dropdown-item>
                            <b-dropdown-item v-for="(practice,i) in otherPractices" :key="i"
                                             @click="switchToPractice(practice)">
                                <shs-icon name="account-login" class="mr-2"></shs-icon>
                                {{ $i18n.gt('LABEL_SWITCH_PRACTICE') }}:
                                {{ practice.name }}
                            </b-dropdown-item>
                        </template>
                    </b-nav-item-dropdown>

                    <!-- dropdown company switch for multi-company admins -->
                    <b-nav-item-dropdown :text="`${$user.email()} (${$user.role()})`" right
                                         v-if="$user.roleIs('MULTI_COMPANY_ADMIN')">
                        <b-dropdown-item>
                            {{ $i18n.gt('LABEL_CURRENT_COMPANY') }}: {{ $store.state.currentUser.company.name }}
                        </b-dropdown-item>
                        <b-dropdown-item v-for="(company, i) in otherCompanies" :key="i"
                                         @click="switchToCompany(company)">
                            <shs-icon name="account-login" class="mr-2"></shs-icon>
                            {{ $i18n.gt('LABEL_SWITCH_COMPANY') }}:
                            {{ company.name }}
                        </b-dropdown-item>

                    </b-nav-item-dropdown>

                    <!-- simple profile link (for participants) -->
                    <b-nav-item :to="`/participant/profile/id/${$user.participantId()}`"
                                class="main-menu__logged-in-as"
                                v-if="$user.userId() && !$user.roleIs('PRACTITIONER') && !$user.roleIs('MULTI_COMPANY_ADMIN')">
                        {{ $user.email() }}
                        <span v-if="!$user.roleIs('PARTICIPANT')">
                            ({{ $user.role() }})
                        </span>
                    </b-nav-item>

                    <b-nav-item v-if="$user.userId() && !$user.isMaskingPractitioner()"
                                @click="logout()">
                        {{ $i18n.gt('LABEL_LOG_OUT') }}
                    </b-nav-item>

                    <b-nav-item v-if="$user.isMasking() && !$user.isMaskingPractitioner()"
                                @click="unmask()">
                        {{ $i18n.gt('LABEL_UNMASK') }}
                    </b-nav-item>

                </b-navbar-nav>
            </b-collapse>
        </b-navbar>

    </div>

</template>

<script>

import Vue from 'vue'
import User from '@/apps/default/User'

/**
 * Displays the top menu with user presence and various commands appropriate to the user's role
 */
export default Vue.extend({
    name: 'UserMenu',
    computed: {
        currentPractice() {
            return this.$user.currentPractice()
        },
        otherPractices() {
            return this.$user.otherPractices()
        },
        otherCompanies() {
            return this.$user.otherCompanies()
        },
        isHomeDisabled() {
            return (this.$route.name === 'home') ? 'disabled' : null
        },
        isHomeTitle() {
            return (this.$route.name === 'home') ? this.$i18n.gt('LABEL_HOME_DISABLED') : null
        },
        inProduction() {
            return this.$api.inProduction
        },
        inDemo() {
            return this.$api.inDemo
        },
    },
    methods: {
        switchLanguage() {
            User.switchLanguage()
        },
        switchToPractice(practice) {
            this.$user.switchToPractice(this, practice)
        },
        switchToCompany(company) {
            this.$user.switchToCompany(this, company)
        },
        logout() {
            User.logoutUser(this)
        },
        unmask() {
            User.unmaskUser(this)
        }
    },
})
</script>

<style lang="scss">

@import '../../sass/base';

.user-menu {
    height: 100%;

    > .navbar {
        padding: 0;
        height: 100%;
    }
}

.main-menu {
    height: 70px;

    > .navbar {
        height: 70px;
    }

    .nav-link {
        font-weight: 300;
    }

    @media (min-width: 992px) {
        //@include transform(translateX(1.25em));
        > .navbar {
            padding-left: 0;
            padding-right: 0;
        }
        .navbar-nav .nav-item {
            &:first-child a.nav-link {
                padding-left: 0;
            }

            a.nav-link {
                padding-left: 1em;
                padding-right: 1em;
                font-size: 14px;
                color: var(--main-menu-item-desktop);
                @include transition(all .3s);

                &:hover, &:focus {
                    color: var(--main-menu-item-hover-desktop);
                    color: var(--blue);
                }
            }
        }
    }
}

.main-menu__environment {
    position: absolute !important;
    top: 26px;
    left: 440px;
    margin-left: -35px;
    padding: .5em 1.5em;
    background-color: #ffc107 !important;
    z-index: 30;

    @media (max-width: 991px) {
        top: 2px;
        left: 60px;
        margin-left: 0;
    }
    @media (min-width: 1200px) {
        left: 560px;
    }
}

.main-menu__logged-in-as {
}

/************** MAIN MENU ************/

@media (max-width: 1023px) {
    .navbar-expand-md .navbar-toggler {
        display: block;
    }
}

.content ul.navbar-nav {
    margin-left: 0;
}

@media (min-width: 768px) {

    .navbar-light .navbar-nav .show > a,
    .navbar-light .navbar-nav .active > a,
    .navbar-light .navbar-nav a.show,
    .navbar-light .navbar-nav a.active {
        color: #fff;
        //background-color: var(--accent-color);
        background-color: transparent;
        cursor: default;

        &:after {
            display: none;
        }
    }
}

.navbar-toggler {
    border: none;
    @include vertical-center;
    padding: 0;
    right: 0;

    .navbar-toggler-icon {
        background-size: 100% auto;
    }
}

@media (max-width: 991px) {
    .navbar-collapse {
        //display:block!important;
        .navbar-nav {
            position: fixed;
            z-index: 100;
            left: 0;
            top: 0;
            height: 100%;
            width: 280px;
            display: block;
            background-color: var(--base-color);
            transform: translateX(-100%);
            margin: 0;
            transition: transform .3s;

            a.nav-link {
                display: block;
                color: #fff;
                padding: 1em;
            }
        }

        &.show > .navbar-nav {
            transform: translateX(0);
        }
    }

    .navbar-light .navbar-nav .show > .nav-link, .navbar-light .navbar-nav .active > .nav-link, .navbar-light .navbar-nav .nav-link.show, .navbar-light .navbar-nav .nav-link.active {
        color: #fff;
        background-color: var(--accent-color);
    }

    .navbar-light .navbar-nav .nav-link:hover, .navbar-light .navbar-nav .nav-link:focus {
        color: #fff;
        background-color: var(--accent-color);
    }
}

</style>
